import { useState, useMemo } from "react";
import { NumberField, Stat, ToolShell } from "./parts";
import { inr } from "@/lib/format";
import { LeadDialog } from "@/components/LeadDialog";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, FileText } from "lucide-react";

const GST_TYPES = {
  affordable: { label: "Under-construction · Affordable (1%)", rate: 1 },
  regular: { label: "Under-construction · Premium (5%)", rate: 5 },
  ready: { label: "Ready-to-move with CC (0%)", rate: 0 },
};

const OTHER_GST = 18; // GST on PLC / parking / club & other charges

export function CostSheet() {
  const [base, setBase] = useState(6000000);
  const [otherCharges, setOtherCharges] = useState(600000);
  const [gstType, setGstType] = useState("regular");
  const [stampRate, setStampRate] = useState(6);
  const [regRate, setRegRate] = useState(1);
  const [misc, setMisc] = useState(50000);

  const rows = useMemo(() => {
    const propGstRate = GST_TYPES[gstType].rate;
    const propGst = (base * propGstRate) / 100;
    const otherGst = (otherCharges * OTHER_GST) / 100;
    const stamp = ((base + otherCharges) * stampRate) / 100;
    const reg = ((base + otherCharges) * regRate) / 100;
    const total = base + otherCharges + propGst + otherGst + stamp + reg + misc;
    return {
      propGstRate,
      list: [
        ["Base Property Cost", base],
        ["Other Charges (PLC, parking, club)", otherCharges],
        [`GST on property @ ${propGstRate}%`, propGst],
        [`GST on other charges @ ${OTHER_GST}%`, otherGst],
        [`Stamp Duty @ ${stampRate}%`, stamp],
        [`Registration @ ${regRate}%`, reg],
        ["Legal & Misc.", misc],
      ],
      total,
    };
  }, [base, otherCharges, gstType, stampRate, regRate, misc]);

  return (
    <ToolShell title="Cost Sheet Builder" desc="The real all-in cost of your home — beyond the sticker price. GST is applied at 1% or 5% on the property (per type) and 18% on other charges like PLC, parking and club membership.">
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="space-y-7">
          <NumberField label="Base Property Cost" value={base} onChange={setBase} min={1000000} max={200000000} step={100000} prefix="₹" testid="cost-base" />
          <NumberField label="Other Charges" value={otherCharges} onChange={setOtherCharges} min={0} max={20000000} step={50000} prefix="₹" testid="cost-other" />
          <div className="space-y-3">
            <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Property GST Type</label>
            <Select value={gstType} onValueChange={setGstType}>
              <SelectTrigger className="tool-input rounded-none" data-testid="cost-gst-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(GST_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="font-mono text-[10px] text-muted-foreground">Affordable (1%) applies to homes under ₹45 lakh. Other charges are taxed at {OTHER_GST}% GST automatically.</p>
          </div>
          <NumberField label="Stamp Duty" value={stampRate} onChange={setStampRate} min={0} max={10} step={0.5} suffix="%" testid="cost-stamp" />
          <NumberField label="Registration" value={regRate} onChange={setRegRate} min={0} max={5} step={0.5} suffix="%" testid="cost-reg" />
          <NumberField label="Legal & Misc." value={misc} onChange={setMisc} min={0} max={2000000} step={10000} prefix="₹" testid="cost-misc" />
        </div>
        <div className="space-y-3">
          <div className="border border-border bg-background" data-testid="cost-breakdown">
            {rows.list.map(([label, val]) => (
              <div key={label} className="flex items-center justify-between border-b border-border px-5 py-4 last:border-0">
                <span className="text-sm text-muted-foreground">{label}</span>
                <span className="font-mono text-sm text-foreground">{inr(val)}</span>
              </div>
            ))}
          </div>
          <Stat label="All-In Cost" value={inr(rows.total)} highlight testid="cost-result" />
          <Link to="/cost-sheet" data-testid="cost-detailed-link">
            <Button className="w-full rounded-none bg-cyan font-mono text-xs uppercase tracking-widest text-white hover:bg-cyan/90">
              <FileText className="mr-2 h-4 w-4" /> Build a detailed booking cost sheet
            </Button>
          </Link>
          <LeadDialog source="cost-sheet" defaultNote="Please send me a detailed cost sheet for my shortlisted property." trigger={
            <Button variant="outline" className="w-full rounded-none border-border font-mono text-xs uppercase tracking-widest hover:border-cyan hover:text-cyan" data-testid="cost-lead-cta">
              <FileDown className="mr-2 h-4 w-4" /> Get a personalised cost sheet
            </Button>
          } />
        </div>
      </div>
    </ToolShell>
  );
}
